import { useState, useCallback } from 'react';
import { parseError, formatErrorMessage } from '@/utils/errorHandler';

/**
 * Hook para manejo consistente de errores en componentes
 *
 * Integra con errorHandler utility para normalizar errores
 * de diferentes fuentes (Supabase, Backend Custom, Network)
 *
 * @returns {Object} Estado y métodos de manejo de errores
 * @returns {Object} return.error_state - Estado del error
 * @returns {boolean} return.error_state.has_error - Si hay error activo
 * @returns {string|null} return.error_state.error_type - Tipo de error
 * @returns {string|null} return.error_state.error_message - Mensaje para UI
 * @returns {Object|null} return.error_state.original_error - Error original
 * @returns {Function} return.setError - Establecer error
 * @returns {Function} return.clearError - Limpiar error
 * @returns {Function} return.handleError - Wrapper para try/catch
 *
 * @example
 * const { error_state, setError, clearError, handleError } = useErrorHandler();
 *
 * // Opción 1: Usar setError directamente
 * const { data, error } = await supabase.from('artworks').select();
 * if (error) {
 *   setError(error);
 * }
 *
 * // Opción 2: Usar handleError como wrapper
 * const fetchData = async () => {
 *   await handleError(async () => {
 *     const { data, error } = await supabase.from('artworks').select();
 *     if (error) throw error;
 *     setArtworks(data);
 *   });
 * };
 *
 * // Renderizar error
 * if (error_state.has_error) {
 *   return <ErrorMessage message={error_state.error_message} />;
 * }
 */
export function useErrorHandler() {
  const [error_state, setErrorState] = useState({
    has_error: false,
    error_type: null,
    error_message: null,
    original_error: null,
  });

  /**
   * Establecer un error
   * @param {*} error - Error a normalizar y establecer
   */
  const setError = useCallback((error) => {
    const normalized = parseError(error);

    // Always use formatErrorMessage to produce user-friendly Spanish messages
    // Never expose raw technical messages (e.g. "Unexpected token '<'") to users
    const user_message = normalized.has_error
      ? formatErrorMessage(normalized)
      : null;

    setErrorState({
      has_error: normalized.has_error,
      error_type: normalized.error_type,
      error_message: user_message,
      original_error: normalized.has_error ? error : null,
    });
  }, []);

  /**
   * Limpiar error actual
   */
  const clearError = useCallback(() => {
    setErrorState({
      has_error: false,
      error_type: null,
      error_message: null,
      original_error: null,
    });
  }, []);

  /**
   * Wrapper para try/catch que maneja errores automáticamente
   * @param {Function} fn - Función async a ejecutar
   * @returns {Promise<boolean>} true si éxito, false si error
   */
  const handleError = useCallback(
    async (fn) => {
      try {
        clearError();
        await fn();
        return true;
      } catch (error) {
        setError(error);
        return false;
      }
    },
    [setError, clearError]
  );

  return {
    error_state,
    setError,
    clearError,
    handleError,
  };
}
